/* eslint-disable react/no-unstable-nested-components, react/jsx-no-useless-fragment */
// The maps below are react-markdown element renderers (not React components in
// the reconciliation sense); defining them inline is the library's intended API.
import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSlug from "rehype-slug";
import {
  Typography,
  Link,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Divider,
  Box,
  Theme
} from "@mui/material";

import MermaidDiagram from "./MermaidDiagram";

interface MarkdownRendererProps {
  /** Raw Markdown content to render. */
  content: string;
}

/** Recursively extract the plain text of a rendered node (for tag detection). */
const getNodeText = (node: React.ReactNode): string => {
  if (typeof node === "string") return node;
  if (Array.isArray(node)) return node.map(getNodeText).join("");
  if (React.isValidElement(node)) return getNodeText(node.props.children);

  return "";
};

// Colored FAQ badges: "PERGUNTA" (primary) and "RESPOSTA" (success).
const faqBadgeSx = (isQuestion: boolean) => (theme: Theme) => ({
  display: "inline-block",
  mr: 1,
  px: 1,
  py: 0.25,
  borderRadius: "4px",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.04em",
  color: "#fff",
  verticalAlign: "middle",
  backgroundColor: isQuestion ? theme.palette.primary.main : theme.palette.success.main
});

const rootSx = {
  "& > *:first-of-type": { mt: 0 }
};

const boxedHeadingSx = (theme: Theme) => ({
  scrollMarginTop: 80,
  mt: 5,
  mb: 2,
  fontWeight: 700,
  p: "12px 16px",
  borderRadius: "6px",
  backgroundColor: theme.palette.mode === "dark" ? "#1a2332" : "#f0f4ff",
  borderLeft: `4px solid ${theme.palette.primary.main}`
});

const h3Sx = (theme: Theme) => ({
  scrollMarginTop: 80,
  mt: 3,
  mb: 1,
  fontWeight: 600,
  color: theme.palette.primary.dark
});

const inlineCodeSx = (theme: Theme) => ({
  fontFamily: "'Fira Code', 'Consolas', monospace",
  fontSize: "0.85em",
  backgroundColor: theme.palette.mode === "dark" ? "#2d2d2d" : "#e8edf5",
  padding: "2px 6px",
  borderRadius: "3px",
  color: theme.palette.primary.dark
});

const codeBlockSx = (theme: Theme) => ({
  p: 2,
  borderRadius: "6px",
  backgroundColor: theme.palette.mode === "dark" ? "#1e1e1e" : "#1e293b",
  overflowX: "auto",
  mb: 2,
  "& code": {
    fontFamily: "'Fira Code', 'Consolas', monospace",
    fontSize: 13,
    color: "#e2e8f0",
    whiteSpace: "pre"
  }
});

const blockquoteSx = (theme: Theme) => ({
  borderLeft: `4px solid ${theme.palette.primary.main}`,
  m: "16px 0",
  p: "12px 16px",
  backgroundColor: theme.palette.mode === "dark" ? "#1a1a2e" : "#eef2ff",
  borderRadius: "0 8px 8px 0",
  "& p": { m: 0 },
  "& p + p": { mt: 1 }
});

const tableSx = (theme: Theme) => ({
  mb: 3,
  borderRadius: "8px",
  overflow: "hidden",
  border: `1px solid ${theme.palette.divider}`,
  "& th": {
    fontWeight: 700,
    backgroundColor: theme.palette.mode === "dark" ? "#1a2332" : "#f0f4ff",
    borderBottom: `2px solid ${theme.palette.primary.light}`
  },
  "& td": { borderBottom: `1px solid ${theme.palette.divider}` },
  "& tr:last-child td": { borderBottom: "none" }
});

/**
 * Render documentation Markdown (GitHub-flavored) as styled Material UI
 * elements. Supports tables, code blocks, blockquotes, links and inline
 * Mermaid diagrams (```mermaid fenced blocks).
 */
const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content }) => (
  <Box sx={rootSx}>
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeSlug]}
      components={{
        h1: ({ children, id }) => (
          <Typography id={id} variant="h4" sx={{ scrollMarginTop: 80, mt: 3, mb: 1, fontWeight: 700 }} gutterBottom>
            {children}
          </Typography>
        ),
        h2: ({ children, id }) => (
          <Typography id={id} variant="h5" sx={boxedHeadingSx} gutterBottom>
            {children}
          </Typography>
        ),
        h3: ({ children, id }) => (
          <Typography id={id} variant="h6" sx={h3Sx} gutterBottom>
            {children}
          </Typography>
        ),
        h4: ({ children, id }) => (
          <Typography id={id} variant="subtitle1" sx={{ scrollMarginTop: 80, mt: 3, mb: 1, fontWeight: 600 }} gutterBottom>
            {children}
          </Typography>
        ),
        p: ({ children }) => {
          const items = React.Children.toArray(children);
          const label = getNodeText(items[0]).trim();
          const isQuestion = label === "PERGUNTA:";
          const isAnswer = label === "RESPOSTA:";

          if (isQuestion || isAnswer) {
            return (
              <Typography variant="body1" sx={{ mb: isAnswer ? 2.5 : 0.5, lineHeight: 1.9 }}>
                <Box component="span" sx={faqBadgeSx(isQuestion)}>
                  {isQuestion ? "PERGUNTA" : "RESPOSTA"}
                </Box>
                {items.slice(1)}
              </Typography>
            );
          }

          return (
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
              {children}
            </Typography>
          );
        },
        a: ({ href, children }) => (
          <Link href={href} target="_blank" rel="noopener noreferrer" color="primary">
            {children}
          </Link>
        ),
        table: ({ children }) => (
          <Table size="small" sx={tableSx}>
            {children}
          </Table>
        ),
        thead: ({ children }) => <TableHead>{children}</TableHead>,
        tbody: ({ children }) => <TableBody>{children}</TableBody>,
        tr: ({ children }) => <TableRow>{children}</TableRow>,
        th: ({ children }) => (
          <TableCell>
            <strong>{children}</strong>
          </TableCell>
        ),
        td: ({ children }) => <TableCell>{children}</TableCell>,
        code: ({ inline, className, children }: any) => {
          const match = /language-(\w+)/.exec(className || "");
          const lang = match ? match[1] : "";

          if (!inline && lang === "mermaid") {
            return <MermaidDiagram chart={String(children).trim()} />;
          }

          if (inline) {
            return <Box component="code" sx={inlineCodeSx}>{children}</Box>;
          }

          return (
            <Paper elevation={2} sx={codeBlockSx}>
              <code>{children}</code>
            </Paper>
          );
        },
        pre: ({ children }) => <>{children}</>,
        blockquote: ({ children }) => (
          <Paper elevation={0} sx={blockquoteSx}>
            {children}
          </Paper>
        ),
        hr: () => <Divider sx={{ my: 4 }} />,
        ul: ({ children }) => (
          <Box
            component="ul"
            sx={{ pl: 3, mb: 2, listStyleType: "disc", listStylePosition: "outside" }}
          >
            {children}
          </Box>
        ),
        ol: ({ children }) => (
          <Box
            component="ol"
            sx={{ pl: 3, mb: 2, listStyleType: "decimal", listStylePosition: "outside", "& > li": { fontWeight: 500 } }}
          >
            {children}
          </Box>
        ),
        li: ({ children }) => (
          <Box component="li" sx={{ mb: 0.5, lineHeight: 1.7, pl: 0.5 }}>
            {children}
          </Box>
        ),
        img: ({ src, alt }) => (
          <Box component="img" src={src} alt={alt} sx={{ maxWidth: "100%", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }} />
        )
      }}
    >
      {content}
    </ReactMarkdown>
  </Box>
);

export default MarkdownRenderer;
